import React, { useState, useEffect, useCallback } from "react";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import api from "../services/api";
import {
  MoneyRecive,
  ShoppingCart,
  Clock,
  Profile2User,
  Warning2,
  Refresh2,
  TrendUp,
  TickCircle,
} from "iconsax-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

const todayStr = () => new Date().toISOString().split("T")[0];

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [chartPeriod, setChartPeriod] = useState("30"); // "7" | "30" | "90"
  const [chartData, setChartData] = useState(null);
  const [period, setPeriod] = useState("today");
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customFrom, setCustomFrom] = useState(todayStr());
  const [customTo, setCustomTo] = useState(todayStr());

  const fmtDate = (dt) => dt.toISOString().split("T")[0];

  const periodPresets = [
    { key: "today", label: "Hari Ini" },
    { key: "yesterday", label: "Kemarin" },
    { key: "week", label: "Minggu Ini" },
    { key: "month", label: "Bulan Ini" },
    { key: "custom", label: "Custom" },
  ];

  const applyPreset = (key) => {
    if (key === "custom") {
      setShowCustomPicker((v) => !v);
      return;
    }
    const now = new Date();
    let from, to;
    if (key === "today") {
      from = to = fmtDate(now);
    } else if (key === "yesterday") {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      from = to = fmtDate(y);
    } else if (key === "week") {
      const day = now.getDay() || 7; // Mon=1...Sun=7
      const mon = new Date(now);
      mon.setDate(now.getDate() - (day - 1));
      from = fmtDate(mon);
      to = fmtDate(now);
    } else if (key === "month") {
      from = fmtDate(new Date(now.getFullYear(), now.getMonth(), 1));
      to = fmtDate(now);
    }
    setPeriod(key);
    setDateFrom(from);
    setDateTo(to);
    setShowCustomPicker(false);
  };

  const applyCustomRange = () => {
    if (customFrom > customTo) {
      return; // silently ignore invalid range
    }
    setPeriod("custom");
    setDateFrom(customFrom);
    setDateTo(customTo);
    setShowCustomPicker(false);
  };

  const fmt2 = (s) =>
    new Date(s + "T00:00:00").toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const periodLabel =
    period === "today"
      ? "Hari Ini"
      : period === "yesterday"
        ? "Kemarin"
        : period === "week"
          ? "Minggu Ini"
          : period === "month"
            ? "Bulan Ini"
            : dateFrom === dateTo
              ? fmt2(dateFrom)
              : `${fmt2(dateFrom)} – ${fmt2(dateTo)}`;

  const loadDashboard = useCallback(async (silent = false, from, to) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.get("/reports/dashboard", {
        params: { date_from: from || dateFrom, date_to: to || dateTo },
      });
      setData(res.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard(false, dateFrom, dateTo);
  }, [dateFrom, dateTo]); // eslint-disable-line

  // Re-slice chart data when period or data changes
  useEffect(() => {
    if (!data?.monthlySales) return;
    const days = parseInt(chartPeriod);
    const sliced = data.monthlySales.slice(-days);
    setChartData({
      labels: sliced.map((d) => {
        if (!d.date) return "";
        const parts = String(d.date).split("-");
        if (parts.length !== 3) return d.date;
        const dt = new Date(
          Number(parts[0]),
          Number(parts[1]) - 1,
          Number(parts[2]),
        );
        return dt.toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "short",
        });
      }),
      datasets: [
        {
          label: "Penjualan",
          data: sliced.map((d) => d.sales),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.08)",
          fill: true,
          tension: 0.4,
          pointRadius: days <= 7 ? 4 : 2,
          pointHoverRadius: 6,
        },
      ],
    });
  }, [data, chartPeriod]);

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 320 }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-2"></div>
          <div className="text-muted small">Memuat dashboard...</div>
        </div>
      </div>
    );
  if (!data)
    return <div className="alert alert-danger">Gagal memuat dashboard</div>;

  const today = data.today || {};
  const lowStock = data.lowStock || [];
  const topServices = data.topServices || [];
  const topProducts = data.topProducts || [];
  const byVehicleType = data.byVehicleType || [];
  const byInventoryCategory = data.byInventoryCategory || [];
  const byPaymentMethod = data.byPaymentMethod || [];

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

  const COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, font: { size: 11 } },
      },
    },
    cutout: "62%",
  };

  const statCards = [
    {
      label:
        period === "today"
          ? "Penjualan Hari Ini"
          : `Penjualan (${periodLabel})`,
      value: fmt(today.total_sales),
      icon: <MoneyRecive size={22} color="#fff" variant="Bold" />,
      bg: "#3b82f6",
      light: "#dbeafe",
    },
    {
      label:
        period === "today"
          ? "Transaksi Hari Ini"
          : `Transaksi (${periodLabel})`,
      value: today.total_transactions ?? 0,
      icon: <ShoppingCart size={22} color="#fff" variant="Bold" />,
      bg: "#10b981",
      light: "#d1fae5",
    },
    {
      label: "Antrian / Proses",
      value: data.pending ?? 0,
      icon: <Clock size={22} color="#fff" variant="Bold" />,
      bg: "#f59e0b",
      light: "#fef3c7",
    },
    {
      label: "Total Customer",
      value: data.totalCustomers ?? 0,
      icon: <Profile2User size={22} color="#fff" variant="Bold" />,
      bg: "#8b5cf6",
      light: "#ede9fe",
    },
  ];

  const fmtTime = (d) =>
    d
      ? d.toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
      : "";

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="fw-bold mb-0">Dashboard</h5>
          {lastUpdated && (
            <small className="text-muted">
              Diperbarui {fmtTime(lastUpdated)}
            </small>
          )}
        </div>
        <button
          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
          onClick={() => loadDashboard(true, dateFrom, dateTo)}
          disabled={refreshing}
          title="Refresh data"
        >
          <Refresh2 size={16} className={refreshing ? "spin-icon" : ""} />
          {refreshing ? "Memuat..." : "Refresh"}
        </button>
      </div>

      {/* Period Filter */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-2 px-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span
              className="text-muted small fw-semibold"
              style={{ marginRight: 4 }}
            >
              Periode:
            </span>
            {periodPresets.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`btn btn-sm ${
                  period === p.key || (p.key === "custom" && showCustomPicker)
                    ? "btn-primary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {showCustomPicker && (
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={customFrom}
                max={todayStr()}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <span className="text-muted small">s/d</span>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={customTo}
                min={customFrom}
                max={todayStr()}
                onChange={(e) => setCustomTo(e.target.value)}
              />
              <button
                className="btn btn-sm btn-primary"
                onClick={applyCustomRange}
              >
                Terapkan
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowCustomPicker(false)}
              >
                Batal
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="row g-3 mb-4">
        {statCards.map((c, i) => (
          <div key={i} className="col-6 col-md-3">
            <div
              className="card border-0 h-100"
              style={{
                background: `linear-gradient(135deg, ${c.bg}ee, ${c.bg}cc)`,
              }}
            >
              <div className="card-body text-white p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 38,
                      height: 38,
                      background: "rgba(255,255,255,0.2)",
                      flexShrink: 0,
                    }}
                  >
                    {c.icon}
                  </div>
                  <TrendUp size={14} color="rgba(255,255,255,0.6)" />
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "1.15rem", lineHeight: 1.1 }}
                >
                  {c.value}
                </div>
                <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>
                  {c.label}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart + Low Stock */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">Tren Penjualan</h6>
                <div className="btn-group btn-group-sm" role="group">
                  {[
                    ["7", "7H"],
                    ["30", "30H"],
                    ["90", "90H"],
                  ].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      className={`btn ${
                        chartPeriod === v
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() => setChartPeriod(v)}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {chartData && (
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (v) =>
                            "Rp " + Number(v).toLocaleString("id-ID"),
                          font: { size: 10 },
                        },
                        grid: { color: "rgba(0,0,0,0.05)" },
                      },
                      x: { grid: { display: false } },
                    },
                  }}
                  style={{ minHeight: 200, maxHeight: 240 }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex flex-column">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Warning2 size={18} color="#f59e0b" variant="Bold" />
                <h6 className="fw-bold mb-0">Stok Menipis</h6>
                {lowStock.length > 0 && (
                  <span className="badge bg-danger rounded-pill ms-auto">
                    {lowStock.length}
                  </span>
                )}
              </div>
              {lowStock.length === 0 ? (
                <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center gap-2">
                  <TickCircle
                    size="20%"
                    color="#10b981"
                    variant="Bold"
                    style={{ minWidth: 64, maxWidth: 96 }}
                  />
                  <div className="text-muted small">Semua stok aman</div>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {lowStock.map((p) => (
                    <div
                      key={p.id}
                      className="list-group-item d-flex justify-content-between align-items-center px-0 py-2"
                    >
                      <div>
                        <div
                          className="small fw-semibold text-truncate"
                          style={{ maxWidth: 150 }}
                        >
                          {p.name}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.7rem" }}
                        >
                          SKU: {p.sku || "-"}
                        </div>
                      </div>
                      <span className="badge bg-danger rounded-pill">
                        {p.stock} {p.unit}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top Services + Products */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Layanan Terlaris Hari Ini</h6>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr className="table-light">
                      <th style={{ width: 24 }}>#</th>
                      <th>Layanan</th>
                      <th className="text-center">Qty</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topServices.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          Belum ada data hari ini
                        </td>
                      </tr>
                    ) : (
                      topServices.map((s, i) => (
                        <tr key={i}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{s.item_name}</td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {s.total_qty}
                            </span>
                          </td>
                          <td className="text-end fw-semibold">
                            {fmt(s.total_sales)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Produk Terlaris Hari Ini</h6>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr className="table-light">
                      <th style={{ width: 24 }}>#</th>
                      <th>Produk</th>
                      <th className="text-center">Qty</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          Belum ada data hari ini
                        </td>
                      </tr>
                    ) : (
                      topProducts.map((p, i) => (
                        <tr key={i}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{p.item_name}</td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {p.total_qty}
                            </span>
                          </td>
                          <td className="text-end fw-semibold">
                            {fmt(p.total_sales)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown Charts */}
      <div className="row g-3">
        {/* Per Jenis Kendaraan */}
        <div className="col-12 col-md-6 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Per Jenis Kendaraan</h6>
              {byVehicleType.length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  Belum ada data hari ini
                </div>
              ) : (
                <>
                  <div style={{ maxWidth: 200, margin: "0 auto" }}>
                    <Doughnut
                      data={{
                        labels: byVehicleType.map((v) => v.vehicle_type),
                        datasets: [
                          {
                            data: byVehicleType.map((v) =>
                              Number(v.total_sales),
                            ),
                            backgroundColor: COLORS,
                          },
                        ],
                      }}
                      options={doughnutOpts}
                    />
                  </div>
                  <div className="mt-3">
                    {byVehicleType.map((v, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between align-items-center py-1 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              background: COLORS[i % COLORS.length],
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span className="small">{v.vehicle_type}</span>
                        </div>
                        <div className="text-end">
                          <div className="small fw-semibold">
                            {fmt(v.total_sales)}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {v.total_qty} unit
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Per Kategori Inventaris */}
        <div className="col-12 col-md-6 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Per Kategori Produk</h6>
              {byInventoryCategory.length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  Belum ada penjualan produk hari ini
                </div>
              ) : (
                <>
                  <div style={{ maxWidth: 200, margin: "0 auto" }}>
                    <Doughnut
                      data={{
                        labels: byInventoryCategory.map((c) => c.category_name),
                        datasets: [
                          {
                            data: byInventoryCategory.map((c) =>
                              Number(c.total_sales),
                            ),
                            backgroundColor: COLORS.slice().reverse(),
                          },
                        ],
                      }}
                      options={doughnutOpts}
                    />
                  </div>
                  <div className="mt-3">
                    {byInventoryCategory.map((c, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between align-items-center py-1 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              background:
                                COLORS[(COLORS.length - 1 - i) % COLORS.length],
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span className="small">{c.category_name}</span>
                        </div>
                        <div className="text-end">
                          <div className="small fw-semibold">
                            {fmt(c.total_sales)}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {c.total_qty} unit
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Per Metode Pembayaran */}
        <div className="col-12 col-md-12 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Per Metode Pembayaran</h6>
              {byPaymentMethod.length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  Belum ada data hari ini
                </div>
              ) : (
                <>
                  <Bar
                    data={{
                      labels: byPaymentMethod.map((pm) =>
                        pm.payment_method === "unpaid"
                          ? "Belum Bayar"
                          : (pm.payment_method || "").toUpperCase(),
                      ),
                      datasets: [
                        {
                          label: "Total",
                          data: byPaymentMethod.map((pm) =>
                            Number(pm.total_sales),
                          ),
                          backgroundColor: COLORS,
                          borderRadius: 6,
                        },
                      ],
                    }}
                    options={{
                      indexAxis: "y",
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: {
                          beginAtZero: true,
                          ticks: {
                            callback: (v) =>
                              "Rp " + Number(v).toLocaleString("id-ID"),
                            font: { size: 10 },
                          },
                          grid: { color: "rgba(0,0,0,0.05)" },
                        },
                        y: { grid: { display: false } },
                      },
                    }}
                  />
                  <div className="mt-2">
                    {byPaymentMethod.map((pm, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between align-items-center py-1 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              background: COLORS[i % COLORS.length],
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span className="small">
                            {pm.payment_method === "unpaid"
                              ? "Belum Bayar"
                              : (pm.payment_method || "").toUpperCase()}
                          </span>
                        </div>
                        <div className="text-end">
                          <div className="small fw-semibold">
                            {fmt(pm.total_sales)}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {pm.total_transactions} trx
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
